import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VentureCard, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { TicketThread } from "@/components/support/TicketThread";
import { MessageInput } from "@/components/support/MessageInput";
import { useTicketMessages, type Ticket } from "@/hooks/useTickets";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

function priorityBadgeVariant(priority: string) {
  switch (priority) {
    case 'urgent': return 'destructive';
    case 'high': return 'warning';
    case 'medium': return 'info';
    case 'low': return 'default';
    default: return 'default';
  }
}

export default function AdminTicketDetail() {
  const navigate = useNavigate();
  const { ticketId } = useParams<{ ticketId: string }>();

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [ticketLoading, setTicketLoading] = useState(true);
  const [senderName, setSenderName] = useState('Admin');
  const [currentStatus, setCurrentStatus] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { messages, loading: messagesLoading, sendMessage, updateStatus } = useTicketMessages(ticketId);

  useEffect(() => {
    if (!ticketId) return;
    async function fetchTicket() {
      setTicketLoading(true);
      try {
        const { data, error } = await supabase
          .from('support_tickets')
          .select('*, clients(company_name)')
          .eq('id', ticketId)
          .single();
        if (error) throw error;
        const mapped: Ticket = {
          ...data,
          client_name: (data as any).clients?.company_name || 'Unknown',
        };
        setTicket(mapped);
        setCurrentStatus(data.status);
      } catch (err) {
        toast.error('Failed to load ticket');
        navigate('/admin/support-tickets');
      } finally {
        setTicketLoading(false);
      }
    }
    fetchTicket();
  }, [ticketId, navigate]);

  useEffect(() => {
    async function fetchAdminName() {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      // Try to get name from admin_users table
      const { data } = await supabase
        .from('admin_users')
        .select('full_name')
        .eq('user_id', session.user.id)
        .single();

      if (data?.full_name) {
        setSenderName(data.full_name);
      } else {
        // Fall back to session metadata
        const meta = session.user.user_metadata;
        setSenderName(meta?.full_name || meta?.name || session.user.email || 'Admin');
      }
    }
    fetchAdminName();
  }, []);

  const handleStatusChange = async (newStatus: string) => {
    if (!ticketId) return;
    setUpdatingStatus(true);
    try {
      await updateStatus(newStatus);
      setCurrentStatus(newStatus);
      setTicket(prev => prev ? { ...prev, status: newStatus } : prev);
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSend = async (message: string, file?: File, isInternal?: boolean) => {
    await sendMessage({
      message,
      senderType: 'admin',
      senderName,
      isInternal,
      attachmentFile: file,
    });
  };

  const handleDelete = async () => {
    if (!ticket) return;
    setDeleting(true);
    try {
      // Delete storage folder for this ticket (best-effort)
      const { data: files } = await supabase.storage
        .from('support-attachments')
        .list(`${ticket.client_id}/${ticket.id}`);

      if (files && files.length > 0) {
        const paths = files.map(f => `${ticket.client_id}/${ticket.id}/${f.name}`);
        await supabase.storage.from('support-attachments').remove(paths);
      }

      // Delete the ticket (CASCADE handles messages)
      const { error } = await supabase
        .from('support_tickets')
        .delete()
        .eq('id', ticket.id);

      if (error) throw error;

      toast.success('Ticket deleted');
      navigate('/admin/support-tickets');
    } catch (err) {
      toast.error('Failed to delete ticket');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (ticketLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-32 rounded bg-muted/30 animate-pulse" />
        <div className="h-64 rounded-xl bg-muted/30 animate-pulse" />
      </div>
    );
  }

  if (!ticket) return null;

  return (
    <motion.div
      className="space-y-4 flex flex-col h-full"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Back Button */}
      <button
        onClick={() => navigate('/admin/support-tickets')}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Support Tickets
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold">{ticket.subject}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {ticket.client_name} &middot; <span className="capitalize">{ticket.category.replace('_', ' ')}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <BadgeVenture variant={priorityBadgeVariant(ticket.priority)} className="text-xs capitalize">
            {ticket.priority}
          </BadgeVenture>
          <BadgeVenture variant="default" className="text-xs capitalize">
            {ticket.category.replace('_', ' ')}
          </BadgeVenture>

          {/* Status Dropdown */}
          <Select value={currentStatus} onValueChange={handleStatusChange} disabled={updatingStatus}>
            <SelectTrigger className="h-8 w-36 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="danger"
            size="sm"
            onClick={() => setDeleteDialogOpen(true)}
            className="h-8"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            Delete
          </Button>
        </div>
      </div>

      {/* Thread + Message Input */}
      <VentureCard className="flex flex-col flex-1 min-h-[400px]">
        <VentureCardContent className="flex flex-col flex-1 p-0 overflow-hidden">
          {messagesLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <TicketThread messages={messages} currentUserType="admin" />
          )}
          <MessageInput onSend={handleSend} showInternalToggle={true} />
        </VentureCardContent>
      </VentureCard>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Ticket</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this ticket? This will permanently remove the ticket and all its messages. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete Ticket'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
